import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { BrandLoader } from '@/components/ui/brand-loader'
import { AuthGuard } from '../components/AuthGuard'
import { AdminGuard } from '../components/AdminGuard'
import { GaleriaGuard } from '../features/artifacts/gallery/GaleriaGuard'
import { GuidedTourLayer } from '../features/guided-tour/GuidedTourLayer'
import ResearchJobsNotifier from '../components/chat/ResearchJobsNotifier'

/**
 * O pedaco do chat, e por que ele tem tratamento diferente dos outros.
 *
 * MEDIDO no build de 05/09/2026: `ChatPage` compila para 957,97 kB (272 kB
 * comprimido) — o maior pedaco de rota do projeto. E o `<Suspense>` que envolve
 * TODAS as rotas troca a arvore inteira, inclusive a barra lateral, por um
 * esqueleto de tela cheia enquanto ele baixa. Voltar de outra rota para o
 * chat, num aparelho de campo, e' essa piscada.
 *
 * O chat nao e' uma tela entre outras: e' A tela (Principio n1). Quem sai dele
 * VAI voltar. Entao o pedaco e' aquecido assim que o navegador fica ocioso — a
 * volta encontra o pedaco em cache e nao ha download nenhum na interacao.
 *
 * Isto NAO conserta o tamanho do pedaco, e nao finge que conserta: tira o
 * download de dentro do clique. Encolher o `ChatPage` e' outro trabalho.
 */
const carregaChatPage = () => import('./ChatPage')
const ChatPage = lazy(carregaChatPage)
const NotFoundPage = lazy(() => import('./NotFoundPage'))

/**
 * Aquece o pedaco do chat quando o navegador nao tem mais o que fazer.
 *
 * `requestIdleCallback` de proposito, e nao um `setTimeout`: aquecer nao pode
 * competir com a primeira pintura nem com o primeiro turno da conversa. Onde o
 * navegador nao tem a funcao (Safari antigo, WebView de aparelho de campo), a
 * queda e' NAO AQUECER — nunca um timer que rouba tempo de quem esta digitando.
 */
function aqueceOPedacoDoChat(): void {
  const ocioso = (globalThis as { requestIdleCallback?: (cb: () => void) => void })
    .requestIdleCallback
  if (typeof ocioso !== 'function') return
  ocioso(() => {
    void carregaChatPage()
  })
}
// admin-auth-fix 2026-06-16 — in-app admin dashboard (reuses the Clerk session).
const AdminDashboardPage = lazy(() => import('./AdminDashboardPage'))
const SignInPage = lazy(() => import('./SignInPage'))
const SignUpPage = lazy(() => import('./SignUpPage'))
const PendingApprovalPage = lazy(() => import('./PendingApprovalPage'))
const TeamSettingsPage = lazy(() => import('../features/team/TeamSettingsPage'))
const RolesSettingsPage = lazy(() => import('../features/roles/RolesSettingsPage'))
const InvitationLandingPage = lazy(() => import('../features/invitations/InvitationLandingPage'))
// Sprint A 2026-05-03 — Self-Serve B2C + Billing.
const OnboardingPlanPage = lazy(() => import('../features/onboarding/OnboardingPlanPage'))
const SettingsBillingPage = lazy(() => import('../features/billing/SettingsBillingPage'))
const MemorySettingsPage = lazy(() => import('../features/memory-settings/MemorySettingsPage'))
const DangerZoneSettingsPage = lazy(() => import('../features/danger-zone/DangerZoneSettingsPage'))
// Card #348 Finding 3 — Preventive Plans screen
const PreventivePlansPage = lazy(() => import('../features/maintenance-plans/PreventivePlansPage'))
// Axis Insights (06/09/2026) — galeria viva dos blocos do painel. Nao e tela
// de produto: fonte dos testes visuais, atras da guarda de producao
// (`galeriaHabilitada`) e sem entrada em menu nenhum.
const InsightsGalleryPage = lazy(() => import('../features/artifacts/gallery/InsightsGalleryPage'))

function LoadingFallback() {
  return <BrandLoader className="h-screen bg-axg-bg" />
}

export default function Router() {
  useEffect(aqueceOPedacoDoChat, [])
  return (
    <BrowserRouter>
      {/*
       * O aviso da busca de plano em segundo plano. Fica AQUI — dentro do
       * roteador e FORA das rotas — porque ele tem de continuar andando em
       * qualquer conversa e em qualquer rota. NÃO é rota nem tela: não desenha
       * nada, só governa o aviso do react-hot-toast que o AppToaster já monta.
       */}
      <ResearchJobsNotifier />
      {/* Tour guiado: sem tela, só abre o balão sobre o chat e sobre /settings/team. */}
      <GuidedTourLayer />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Public auth routes */}
          <Route path="/sign-in/*" element={<SignInPage />} />
          <Route path="/sign-up/*" element={<SignUpPage />} />
          <Route path="/pending-approval" element={<PendingApprovalPage />} />
          <Route path="/invitations/:token" element={<InvitationLandingPage />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <AuthGuard>
                <Navigate to="/chat" replace />
              </AuthGuard>
            }
          />
          <Route
            path="/chat"
            element={
              <AuthGuard>
                <ChatPage />
              </AuthGuard>
            }
          />
          <Route
            path="/chat/:conversationId"
            element={
              <AuthGuard>
                <ChatPage />
              </AuthGuard>
            }
          />
          <Route
            path="/settings/team"
            element={
              <AuthGuard>
                <TeamSettingsPage />
              </AuthGuard>
            }
          />
          <Route
            path="/settings/roles"
            element={
              <AuthGuard>
                <RolesSettingsPage />
              </AuthGuard>
            }
          />
          {/* Sprint A — billing self-serve flow */}
          <Route
            path="/onboarding/plan"
            element={
              <AuthGuard>
                <OnboardingPlanPage />
              </AuthGuard>
            }
          />
          <Route
            path="/settings/billing"
            element={
              <AuthGuard>
                <SettingsBillingPage />
              </AuthGuard>
            }
          />
          {/* Sprint B (2026-05-03) — memory management surface */}
          <Route
            path="/settings/memory"
            element={
              <AuthGuard>
                <MemorySettingsPage />
              </AuthGuard>
            }
          />
          <Route
            path="/settings/danger"
            element={
              <AuthGuard>
                <DangerZoneSettingsPage />
              </AuthGuard>
            }
          />
          {/* Card #348 Finding 3 — Preventive Plans audit screen */}
          <Route
            path="/plans"
            element={
              <AuthGuard>
                <PreventivePlansPage />
              </AuthGuard>
            }
          />
          {/* Axis Insights — galeria de blocos, fora de producao (ver GaleriaGuard).
              Sem AuthGuard de proposito: so fixtures, nenhum dado de inquilino, e a
              bateria de screenshot/axe roda contra ela sem sessao do Clerk. */}
          <Route
            path="/dev/insights-gallery"
            element={
              <GaleriaGuard>
                <InsightsGalleryPage />
              </GaleriaGuard>
            }
          />
          {/* admin-auth-fix 2026-06-16 — in-app admin (AdminGuard reuses the
              live Clerk session; engine requireAdmin is the real gate). */}
          <Route
            path="/admin"
            element={
              <AdminGuard>
                <AdminDashboardPage />
              </AdminGuard>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
